export interface VehicleData {
  registrationNumber: string; // Immatriculation (A)
  vin: string; // Numéro d'identification (E)
  firstRegistrationDate: string; // Date 1ère immatriculation (B)
  brand: string; // Marque (D.1)
  type: string; // D.2 (Type / Variante / Version)
  model: string; // D.3 (Désignation commerciale)
  genre: string; // J.1 (Genre national)
  mileage: string; // Kilométrage
  ciPresent?: boolean; // Certificat d'immatriculation présent
  ciFormulaNumber?: string; // n° de formule (AB-123-CD)
  ciIssueDate?: string; // (I) date du CI ancien format
  ciAbsenceReason?: string; // Motif si CI absent
}

export interface PersonData {
  isCompany: boolean;
  companyName: string; // Pour personne morale
  lastName: string;
  firstName: string;
  birthDate?: string;
  birthPlace?: string;
  gender?: string;
  nationality?: string;
  documentNumber?: string;
  role?: string;
  siret: string; // Pour professionnel
  address: string;
  addressLine1?: string;
  addressLine2?: string;
  streetNumber?: string;
  streetName?: string;
  addressComplement?: string;
  country?: string;
  postalCode: string;
  city: string;
}

export interface GarageProfile {
  name: string;
  siret: string;
  address: string;
  postalCode: string;
  city: string;
  country?: string;
  logoData?: string; // base64 data URL pour le logo du garage
}

export interface DossierSummary {
  id: number;
  titre?: string;
  immatriculation?: string;
  vin?: string;
  client_nom?: string;
  client_prenom?: string;
  statut?: string;
  updated_at?: string;
}

export interface DossierDocument {
  id: number;
  type: string;
  filename: string;
  url: string;
  created_at?: string;
}

export interface DossierDetail extends DossierSummary {
  notes?: string;
  documents: DossierDocument[];
  cerfa_state?: CerfaState | null;
  valuation?: ValuationResult | null;
  ct_data?: ControlTechniqueData | null;
}

export interface ValuationResult {
  market_low: number;
  market_high: number;
  market_median: number;
  tradein: number;
  computed_at: string;
  source?: string;
}

export interface CerfaState {
  vehicle: VehicleData;
  seller: PersonData;
  buyer: PersonData;
  saleDate: string;
  saleTime: string;
  isCessionForDestruction: boolean;
  agreedToConditions: boolean;
  vhuApprovalNumber?: string; // n° d'agrément VHU si destruction
}

export interface ControlTechniqueDefect {
  severity?: string;
  label?: string;
  code?: string;
}

export interface ControlTechniqueData {
  registration_number?: string;
  vin?: string;
  mileage_km?: number | null;
  ct_date?: string;
  valid_until?: string;
  result?: string;
  pv_number?: string;
  center_name?: string;
  center_siret?: string;
  defects?: ControlTechniqueDefect[];
  defects_minor?: ControlTechniqueDefect[];
  defects_major?: ControlTechniqueDefect[];
  defects_critical?: ControlTechniqueDefect[];
  observations?: string;
}

export enum AppStep {
  WELCOME = 'WELCOME',
  UPLOAD_CARTE_GRISE = 'UPLOAD_CARTE_GRISE',
  UPLOAD_ID = 'UPLOAD_ID',
  REVIEW = 'REVIEW',
  COMPLETED = 'COMPLETED',
}

export const INITIAL_PERSON: PersonData = {
  isCompany: false,
  companyName: '',
  lastName: '',
  firstName: '',
  birthDate: '',
  birthPlace: '',
  gender: '',
  nationality: '',
  documentNumber: '',
  role: '',
  siret: '',
  address: '',
  addressLine1: '',
  addressLine2: '',
  streetNumber: '',
  streetName: '',
  addressComplement: '',
  country: '',
  postalCode: '',
  city: '',
};

export const INITIAL_STATE: CerfaState = {
  vehicle: {
    registrationNumber: '',
    vin: '',
    firstRegistrationDate: '',
    brand: '',
    type: '',
    model: '',
    genre: '',
    mileage: '',
    ciPresent: true,
    ciFormulaNumber: '',
    ciIssueDate: '',
    ciAbsenceReason: '',
  },
  seller: { ...INITIAL_PERSON, role: 'seller' },
  buyer: { ...INITIAL_PERSON, role: 'buyer' },
  saleDate: new Date().toISOString().split('T')[0],
  saleTime: new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
  isCessionForDestruction: false,
  agreedToConditions: false,
};

export interface LabelingBlock {
  text: string;
  conf: number;
  box: number[][];
  field: string;
  predicted_field?: string;
  /**
   * Liste optionnelle des champs détectés / suggérés par le modèle.
   * Permet à l'UI d'afficher un dropdown de suggestions au lieu d'un simple bouton.
   */
  predicted_candidates?: string[];
  /**
   * Marque un bloc qui ne doit pas être utilisé (bruit, OCR erroné…).
   */
  ignored?: boolean;
}

export interface UserSession {
  email: string;
  name?: string;
  picture?: string;
  is_admin?: boolean;
  plan?: string;
  is_paying?: boolean;
}

export interface LabelingSample {
  doc_type: string;
  page_width?: number;
  page_height?: number;
  image_url?: string;
  /**
   * Liste des champs autorisés pour ce type de document (carte grise, CNI…).
   * Permet à l'UI de limiter les choix et d'éviter les fautes de frappe.
   */
  allowed_fields?: string[];
  blocks: LabelingBlock[];
  ignored_blocks?: LabelingBlock[];
  source?: {
    filename?: string;
    saved_at?: string;
  };
}

export interface LabelingTaskMeta {
  id: string;
  filename: string;
  doc_type?: string;
  blocks: number;
  page_width?: number;
  page_height?: number;
  source?: {
    filename?: string;
    saved_at?: string;
  };
  updated_at?: string;
  image_url?: string;
}
